import {Key, ReactNode} from 'react';

import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {AppParametricSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {SettingsHomeDestinationsEnum} from '../../../settings/home/settingsHomeTypes';
import {AppState} from '../../../store/appState';
import {AppStore} from '../../../store/appStore';
import {OnboardingGuidesEnum} from '../guide/onboardingGuideTypes';

/*
 * Constants.
 */

export enum FluSetupGuideTaskNameEnum {
  SEND_FIRST_EMAIL = 'send-first-email',
  DOWNLOAD_APP = 'download-app',
}

export enum AdminSetupGuideTaskNameEnum {}

export type SetupGuideTaskName = FluSetupGuideTaskNameEnum | AdminSetupGuideTaskNameEnum;

/*
 * Types.
 */

export interface OnboardingTaskStartContext {
  dispatch: AppStore['dispatch'];
  state: AppState;
  panelId: AppPanelId | undefined;
  isQuickInviteTeammatesAvailable?: boolean;
  openSettingsHomeShortcut(destination: SettingsHomeDestinationsEnum): void;
  setOnboardingGuide(onboardingGuide: OnboardingGuidesEnum | undefined): void;
}

export interface SetupGuideTaskDefinition {
  key: Key;
  title: ReactNode;
  description: ReactNode;
  details?: ReactNode;
  isExternal?: boolean;
  startTask(context: OnboardingTaskStartContext): Promise<void> | void;
  computeTrackingObject: (isTaskComplete: boolean | undefined) => string;
  isUnnumbered?: boolean;
}

export type CompletedCourses = {[uuid: string]: boolean};

export interface SetupGuideScreenTask {
  key: React.Key;
  number: number | undefined;
  title: ReactNode;
  description: ReactNode;
  details: ReactNode;
  isComplete: boolean | undefined;
  isExternal: boolean;
  startTask: SetupGuideTaskDefinition['startTask'];
}

export interface SetupGuideScreenProgress {
  totalSteps: number;
  completedSteps: number;
  hasCompletedAllTasks: boolean;
}

export interface SetupGuideScreenTasksAndProgress {
  tasks: ReadonlyArray<SetupGuideScreenTask>;
  progress: SetupGuideScreenProgress;
}

export interface AdminSetupGuideTasksAndProgress {
  demos: SetupGuideScreenTasksAndProgress;
  setup: SetupGuideScreenTasksAndProgress;
}

export interface AdminSetupGuideTaskDefinition extends SetupGuideTaskDefinition {
  getIsTaskVisible: AppParametricSelector<AdminSetupGuideTaskSelectorParams, boolean>;
  getIsTaskComplete: AppParametricSelector<AdminSetupGuideTaskSelectorParams, boolean | undefined>;
}

interface AdminSetupGuideTaskDefinitions {
  demos: ReadonlyArray<AdminSetupGuideTaskDefinition>;
  setup: ReadonlyArray<AdminSetupGuideTaskDefinition>;
}

export interface AdminSetupGuideTaskSelectorParams {
  taskDefinitions: AdminSetupGuideTaskDefinitions;
  completedCourses: CompletedCourses | undefined;
  shouldAssumeCoursesAreComplete?: boolean;
}

export interface FluSetupGuideTasksAndProgress {
  start: SetupGuideScreenTasksAndProgress;
}

export interface FluSetupGuideTaskDefinition extends SetupGuideTaskDefinition {
  getIsTaskVisible: AppParametricSelector<FluSetupGuideTaskSelectorParams, boolean>;
  getIsTaskComplete: AppParametricSelector<FluSetupGuideTaskSelectorParams, boolean | undefined>;
}

interface FluSetupGuideTaskDefinitions {
  start: ReadonlyArray<FluSetupGuideTaskDefinition>;
}

export interface FluSetupGuideTaskSelectorParams {
  taskDefinitions: FluSetupGuideTaskDefinitions;
  panelId: AppPanelId | undefined;
}
